import React, { useEffect, useMemo, useState } from 'react';

import { LineChart } from '@/components/shared/LineChart';
import { ModalSectionCard } from '@/components/shared/ModalSectionCard';
import { ModalSectionSecondaryText } from '@/components/shared/ModalSectionSecondaryText';
import { ModalSectionTitle } from '@/components/shared/ModalSectionTitle';
import { TeamSongTags } from '@/components/TeamSongTags';
import { TeamSongThumbs } from '@/components/TeamSongThumbs';
import { classNames } from '@/helpers/classNames';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import { linearGradientDef } from '@nivo/core';

import ExampleChartLabel from '../ExampleChartLabel/ExampleChartLabel';
import { demoLineData } from './demoLineData';
import styles from './EnergyFlow.module.scss';

type LineData = {
	x: number;
	y: number;
}[];

const HIGH_VALUE = 3;
const MID_VALUE = 2;
const LOW_VALUE = 1;

const EnergyFlow: React.FC<{
	teamSongIds: string[];
}> = ({ teamSongIds }) => {
	const { findTeamSongsById } = useTeamSongs();
	const [lineData, setLineData] = useState<LineData>([]);
	const setlistTeamSongs = findTeamSongsById(teamSongIds);

	const tags = setlistTeamSongs.map((ts) => ts?.energyTags[0]);

	const flow = useMemo(() => {
		return tags.reduce<{ x: number; y: number }[]>((acc, t, i) => {
			// 0, 2, 4, 6 ...
			const index = i * 4;
			const previous: (typeof acc)[number] | undefined = acc[index - 1];
			switch (t) {
				case 'upbeat':
					return [
						...acc,
						{
							x: index + 1,
							y: previous?.y || HIGH_VALUE,
						},
						{
							x: index + 2,
							y: HIGH_VALUE,
						},
						{
							x: index + 3,
							y: HIGH_VALUE,
						},
						{
							x: index + 4,
							y: HIGH_VALUE,
						},
					];
				case 'settling':
					return [
						...acc,
						{
							x: index + 1,
							y: previous?.y
								? previous.y < MID_VALUE
									? MID_VALUE
									: previous.y
								: MID_VALUE,
						},
						{
							x: index + 2,
							y: MID_VALUE,
						},
						{
							x: index + 3,
							y: LOW_VALUE,
						},
						{
							x: index + 4,
							y: LOW_VALUE,
						},
					];
				case 'building':
					return [
						...acc,
						{
							x: index + 1,
							y: previous?.y
								? previous.y > MID_VALUE
									? MID_VALUE
									: previous.y
								: MID_VALUE,
						},
						{
							x: index + 2,
							y: MID_VALUE,
						},
						{
							x: index + 3,
							y: HIGH_VALUE,
						},
						{
							x: index + 4,
							y: HIGH_VALUE,
						},
					];
				case 'reflective':
					return [
						...acc,
						{
							x: index + 1,
							y: previous?.y || LOW_VALUE,
						},
						{
							x: index + 2,
							y: LOW_VALUE,
						},
						{
							x: index + 3,
							y: LOW_VALUE,
						},
						{
							x: index + 4,
							y: LOW_VALUE,
						},
					];
				default:
					return [
						...acc,
						{
							x: index + 1,
							y: previous?.y || MID_VALUE,
						},
						{
							x: index + 2,
							y: MID_VALUE,
						},
						{
							x: index + 3,
							y: MID_VALUE,
						},
						{
							x: index + 4,
							y: MID_VALUE,
						},
					];
			}
		}, []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tags.join()]);

	// useEffect(() => {
	// 	setLineData(flow)
	// }, [flow]);

	// console.log(flow);

	return (
		<div className={styles['root']}>
			<ModalSectionCard paddingSize="lg">
				<ModalSectionTitle>Energy</ModalSectionTitle>
				{teamSongIds.length > 1 ? (
					<>
						<TeamSongTags
							style={{
								justifyContent: 'space-around',
							}}
							teamSongIds={teamSongIds}
						/>
						<CustomLineChart lineData={flow} />

						<TeamSongThumbs
							style={{
								justifyContent: 'space-around',
							}}
							teamSongIds={teamSongIds}
						/>
					</>
				) : (
					<>
						<ModalSectionSecondaryText className="mb-xl">
							Add more songs to see the flow of your setlist
						</ModalSectionSecondaryText>
						<div className={styles['wrapper']}>
							<CustomLineChart
								className="mt-lg"
								lineData={demoLineData}
							/>
							<ExampleChartLabel />
						</div>
					</>
				)}
			</ModalSectionCard>
		</div>
	);
};

function CustomLineChart({
	lineData,
	className,
}: {
	lineData: LineData;
	className?: string;
}) {
	const { isDarkModeOn } = useDarkMode();

	return (
		<LineChart
			curve="basis"
			animate={false}
			wrapperClassName={classNames([
				className,
				'mt-sm',
				styles['line-chart'],
			])}
			data={[
				{
					id: 'flow',
					data: lineData,
				},
			]}
			colors={[isDarkModeOn ? '#b5c4ff' : '#1340fd']}
			maxValue={3}
			enablePoints={false}
			margin={{
				bottom: 80,
				right: 1,
				top: 5,
			}}
			defs={[
				// using helpers
				// will inherit colors from current element
				linearGradientDef('gradientA', [
					{ offset: 0, color: 'inherit', opacity: 0.5 },
					{
						offset: 100,
						color: 'inherit',
						opacity: 0,
					},
				]),
			]}
			theme={{
				grid: {
					line: {
						strokeDasharray: 0,
					},
				},
			}}
			enableArea={true}
			fill={[
				// match using object query
				{ match: '*', id: 'gradientA' },
			]}
			lineWidth={3}
			noAxisBottom
			axisLeft={null}
			isInteractive={false}
		/>
	);
}

export default EnergyFlow;
